import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { e2eLocators } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/credential-forms.constants';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CanPipe } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  CardComponent,
  CardGroupComponent,
  CardHeaderComponent,
  CheckboxComponent,
  FormGroupComponent,
  InputLabelComponent,
  ModalService,
  RadioComponent
} from 'mbs-ui-kit';
import { StorageType } from 'mbs-ui-shared';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { StorageDisplayNamePipe } from 'mbs-ui-shared@utils/pipes/storage-display-name.pipe';
import { from, of } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-s3-compatible-additional-options',
  templateUrl: './s3-compatible-additional-options.component.html',
  styleUrls: ['../credential-forms.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgbAccordionModule,
    CardComponent,
    CardGroupComponent,
    CardHeaderComponent,
    CheckboxComponent,
    CommonModule,
    FormGroupComponent,
    InputLabelComponent,
    RadioComponent,
    ReactiveFormsModule,
    CanPipe,
    StorageDisplayNamePipe,
    I18NextPipe,
    HelpLinkDirective
  ]
})
export class S3CompatibleAdditionalOptionsComponent implements OnInit {
  protected readonly e2eLocators = e2eLocators;
  public readonly StorageType = StorageType;
  public modalService = inject(ModalService);
  public i18N = inject(I18NextPipe);

  @Input() storageType: StorageType.S3Compatible | StorageType.Minio;
  @Input() form: FormGroup;
  @Input() isNativeMultipartControlShown = false;
  @ViewChild('changeToHostStyle', { static: true, read: TemplateRef }) changeToHostStyle: TemplateRef<any>;

  private wasHostStyleModalShown = false;

  ngOnInit() {
    this.useVHostRequestStyle.valueChanges
      .pipe(
        untilDestroyed(this),
        filter((val) => val && !this.wasHostStyleModalShown),
        tap(() => (this.wasHostStyleModalShown = true)),
        switchMap(() => {
          return from(
            this.modalService.open(
              {
                header: {
                  title: this.i18N.transform('storages:addStorageAccountWizard.inputCredentialsStep.changeToHostStyleTitle')
                },
                footer: {
                  okButton: {
                    text: this.i18N.transform('shared:buttons.confirmAndProceed')
                  }
                }
              },
              this.changeToHostStyle
            )
          );
        }),
        catchError(() => of(false))
      )
      .subscribe((val) => {
        !val && this.useVHostRequestStyle.patchValue(false, { emitEvent: false });
      });
  }

  get helpLink(): string {
    switch (this.storageType) {
      case StorageType.Minio:
        return '/billing-storage/storage-providers/MinIO/add-minio-account';
      case StorageType.S3Compatible:
        return '/billing-storage/storage-providers/s3compatible/add-s3-account';
      default:
        return '';
    }
  }

  get isSyntheticBackupAllowed(): FormControl<boolean> {
    return this.form.get('isSyntheticBackupAllowed') as FormControl;
  }

  get isVersioningEnabled(): FormControl<boolean> {
    return this.form.get('isVersioningEnabled') as FormControl;
  }

  get isPrivateStorage(): FormControl<boolean> {
    return this.form.get('isPrivateStorage') as FormControl;
  }

  get useSignatureVersion4(): FormControl<boolean> {
    return this.form.get('useSignatureVersion4') as FormControl;
  }

  get useNativeMultipartUpload(): FormControl<boolean> {
    return this.form.get('useNativeMultipartUpload') as FormControl;
  }

  get keepCredentialsOnEndpoint(): FormControl<boolean> {
    return this.form.get('keepCredentialsOnEndpoint') as FormControl;
  }

  get ignoreCertificate(): FormControl<boolean> {
    return this.form.get('ignoreCertificate') as FormControl;
  }

  get useVHostRequestStyle(): FormControl<boolean> {
    return this.form.get('useVHostRequestStyle') as FormControl;
  }
}
